import { Box, Group, Stack, Text, Title } from "@mantine/core";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  AUTH_FORM_COLUMN,
  AUTH_GRID,
  AUTH_HERO,
  AUTH_HERO_LOGO_HEIGHT,
  AUTH_HERO_TAGLINE,
  AUTH_MOBILE_LOGO_HEIGHT,
  AUTH_COLUMN_ALIGNMENT,
  GLASS_CARD,
  GLASS_SECTION,
  useAuthDocumentBackdrop,
  withoutPadding,
} from "./authLayout";
import "./authAnimations.css";
import { BrandLogo } from "./BrandLogo";
import { axioTokens } from "../theme";

export interface OnboardingHighlight {
  icon: React.ComponentType<{ size?: number; color?: string }>;
  text: string;
}

export type OnboardingRole = "hub" | "recruiter" | "hr" | "affiliate" | "pending";

interface OnboardingShellProps {
  role: OnboardingRole;
  heroTitle: ReactNode;
  heroSubtitle: string;
  highlights?: OnboardingHighlight[];
  statLabel?: string;
  statValue?: string;
  activeStep?: number;
  steps?: string[];
  children: ReactNode;
  footer?: ReactNode;
  accent?: "violet" | "blue";
  /** Vertically center the right panel (hub / pending) */
  centerPanel?: boolean;
  /** Skip the outer white card wrapper (hub uses its own cards) */
  barePanel?: boolean;
}

const DEFAULT_STEPS = ["Choose role", "Verify with Google", "Accept terms", "Submit"];

const ACCENT_COLORS = {
  violet: { main: axioTokens.violet, soft: axioTokens.violetHeroSoft, bg: axioTokens.roleVioletBg, border: axioTokens.whiteAlpha40 },
  blue: { main: axioTokens.roleBlue, soft: axioTokens.roleBlueLighter, bg: axioTokens.roleBlueBg, border: axioTokens.blueBorderAlpha50 },
};

const PANEL_WIDTH: Record<OnboardingRole, number> = {
  hub: axioTokens.unit[1120],
  recruiter: axioTokens.unit[640],
  affiliate: axioTokens.unit[640],
  hr: axioTokens.unit[640],
  pending: axioTokens.unit[520],
};

function StepProgress({
  active,
  accent,
  steps,
}: {
  active: number;
  accent: "violet" | "blue";
  steps: string[];
}) {
  const colors = ACCENT_COLORS[accent];
  return (
    <Stack gap="sm" mb="xl" className="staggerEnter" style={{ animationDelay: axioTokens.duration.instant }}>
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="var(--axio-unit-8)">
        <Text size="xs" fw="var(--axio-font-weight-semibold)" c="dimmed" tt="uppercase" style={{ letterSpacing: axioTokens.letterSpacing.caps }}>
          Step {active + 1} of {steps.length}
        </Text>
        <Text size="sm" fw="var(--axio-font-weight-semibold)" c={colors.main} ta="end" style={{ minWidth: "var(--axio-unit-0)", flex: "var(--axio-number-one)" }}>
          {steps[active]}
        </Text>
      </Group>
      <Group gap="var(--axio-unit-6)" grow>
        {steps.map((step, i) => (
          <Box
            key={step}
            className={`stepPill ${i <= active ? "stepPillActive" : ""}`}
            h="var(--axio-unit-6)"
            style={{
              background: i <= active
                ? accent === "violet"
                  ? axioTokens.stepVioletGradient
                  : axioTokens.stepBlueGradient
                : axioTokens.progressTrack,
            }}
          />
        ))}
      </Group>
    </Stack>
  );
}

export function OnboardingSection({
  title,
  description,
  children,
  accent = "violet",
}: {
  title: string;
  description?: string;
  children: ReactNode;
  accent?: "violet" | "blue";
}) {
  const colors = ACCENT_COLORS[accent];
  return (
    <Box className="onboardingSection" style={{ ...withoutPadding(GLASS_SECTION), borderColor: colors.border }}>
      <Stack gap="md">
        <div>
          <Text fw="var(--axio-font-weight-bold)" size="sm" c={colors.main}>
            {title}
          </Text>
          {description && (
            <Text size="sm" c="dimmed" mt="var(--axio-unit-4)">
              {description}
            </Text>
          )}
        </div>
        {children}
      </Stack>
    </Box>
  );
}

export function OnboardingShell({
  role,
  heroTitle,
  heroSubtitle,
  highlights = [],
  statLabel,
  statValue,
  activeStep,
  steps = DEFAULT_STEPS,
  children,
  footer,
  accent = "violet",
  centerPanel = role === "hub" || role === "pending",
  barePanel = role === "hub",
}: OnboardingShellProps) {
  useAuthDocumentBackdrop();

  const showProgress = role !== "hub" && role !== "pending" && activeStep !== undefined;
  const colors = ACCENT_COLORS[accent];
  const panelWidth = PANEL_WIDTH[role];
  const auroraClass = accent === "blue" ? "auroraBlue" : "aurora";

  return (
    <Box className="authGrid" style={AUTH_GRID}>
      {/* Hero rail — animated aurora */}
      <Box
        component="aside"
        aria-label="AxioHire onboarding guidance"
        visibleFrom="md"
        className={`${auroraClass} authHeroColumn`}
        style={{
          ...AUTH_HERO,
          display: "flex",
          flexDirection: "column",
          justifyContent: AUTH_COLUMN_ALIGNMENT.center,
          alignItems: AUTH_COLUMN_ALIGNMENT.top,
        }}
      >
        {/* Decorative orbs */}
        <div className="heroOrb1" aria-hidden />
        <div className="heroOrb2" aria-hidden />

        <Stack gap="var(--axio-unit-36)" maw="var(--axio-unit-400)" align={AUTH_COLUMN_ALIGNMENT.top} style={{ position: "relative", zIndex: "var(--axio-number-one)" }}>
          <div>
            <BrandLogo variant="full" scheme="dark" height={AUTH_HERO_LOGO_HEIGHT} to={null} />
            {role !== "hub" && (
              <Text c={axioTokens.whiteAlpha55} size="sm" mt="var(--axio-unit-12)">
                <Link to="/join" style={{ color: axioTokens.whiteAlpha78, textDecoration: "underline" }}>
                  Change role
                </Link>
              </Text>
            )}
          </div>

          <Stack gap="sm">
            <Title order={2} style={AUTH_HERO_TAGLINE}>
              {heroTitle}
            </Title>
            <Text c={axioTokens.whiteAlpha68} size="sm" lh={axioTokens.lineHeight.airy} maw="var(--axio-unit-340)">
              {heroSubtitle}
            </Text>
          </Stack>

          {statValue && (
            <Box
              style={{
                display: "inline-block",
                padding: "var(--axio-unit-18) var(--axio-unit-22)",
                borderRadius: "var(--axio-radius)",
                background: axioTokens.whiteAlpha08,
                border: `${axioTokens.unit[1]}px solid ${axioTokens.whiteAlpha12}`,
                backdropFilter: `blur(${axioTokens.dimension.blurMd})`,
                alignSelf: "flex-start",
              }}
            >
              <Text c={axioTokens.whiteAlpha55} size="xs" tt="uppercase" fw="var(--axio-font-weight-bold)" style={{ letterSpacing: axioTokens.letterSpacing.wide }}>
                {statLabel}
              </Text>
              <Text
                c={axioTokens.surface}
                size="xl"
                fw="var(--axio-font-weight-extra-bold)"
                mt="var(--axio-unit-4)"
                style={{ fontFamily: axioTokens.fontHead, lineHeight: axioTokens.lineHeight.tight }}
              >
                {statValue}
              </Text>
            </Box>
          )}

          {highlights.length > 0 && (
            <Stack gap="lg" mt="var(--axio-unit-4)">
              {highlights.map(({ icon: Icon, text }) => (
                <Group key={text} gap="md" wrap="nowrap" align="flex-start">
                  <Box
                    style={{
                      width: "var(--axio-unit-40)",
                      height: "var(--axio-unit-40)",
                      borderRadius: "var(--axio-radius)",
                      background: axioTokens.whiteAlpha10,
                      backdropFilter: `blur(${axioTokens.dimension.blurMd})`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: "var(--axio-number-zero)",
                    }}
                  >
                    <Icon size={axioTokens.unit[20]} color={colors.soft} />
                  </Box>
                  <Text c={axioTokens.whiteAlpha90} size="sm" lh={axioTokens.lineHeight.airy} pt="var(--axio-unit-2)">
                    {text}
                  </Text>
                </Group>
              ))}
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Content column */}
      <Box
        component="main"
        className="formColumnBg authScrollColumn onboardingFormColumn"
        style={{
          ...withoutPadding(AUTH_FORM_COLUMN),
          alignItems: "flex-start",
          justifyContent: "center",
        }}
      >
        <Box
          className={centerPanel ? "onboardingPanel onboardingPanelCentered" : "onboardingPanel"}
          style={{ maxWidth: panelWidth }}
        >
          <Box
            hiddenFrom="md"
            mb={centerPanel ? "xl" : "lg"}
            ta="center"
            w="100%"
            style={{ display: "flex", justifyContent: "center" }}
          >
            <BrandLogo variant="full" scheme="light" height={AUTH_MOBILE_LOGO_HEIGHT} to={null} />
          </Box>

          {showProgress && <StepProgress active={activeStep!} accent={accent} steps={steps} />}

          {barePanel ? (
            children
          ) : (
            <Box className="staggerEnter onboardingGlassCard" style={{ ...withoutPadding(GLASS_CARD), animationDelay: "var(--axio-duration-stagger1)" }}>
              {children}
            </Box>
          )}

          {footer && (
            <Text component="div" ta="center" mt="xl" size="sm" c="dimmed" lh="var(--axio-line-height-relaxed)" className="staggerEnter" style={{ animationDelay: "var(--axio-duration-standard)" }}>
              {footer}
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
}
